import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import AddIcon from "@mui/icons-material/Add";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ReplyIcon from "@mui/icons-material/Reply";
import CallIcon from "@mui/icons-material/Call";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";

// Icon shown in place of the actual file for IMAGE/VIDEO/DOCUMENT headers —
// this preview only knows the template's structure, not the per-recipient
// media itself (that's picked in TemplateVariablesForm), so it mirrors
// Inbox's TemplateSendPreview.jsx placeholder-icon approach.
const headerMediaIcon = (format) => {
  switch (format) {
    case "IMAGE":
      return <ImageOutlinedIcon sx={{ fontSize: 32, color: "text.secondary" }} />;
    case "VIDEO":
      return <VideocamIcon sx={{ fontSize: 32, color: "text.secondary" }} />;
    case "DOCUMENT":
      return <PictureAsPdfOutlinedIcon sx={{ fontSize: 32, color: "text.secondary" }} />;
    default:
      return null;
  }
};

const buttonIcon = (type) => {
  switch (type?.toUpperCase()) {
    case "PHONE_NUMBER":
      return <CallIcon sx={{ fontSize: 15 }} />;
    case "URL":
      return <LaunchIcon sx={{ fontSize: 15 }} />;
    case "COPY_CODE":
      return <ContentCopyIcon sx={{ fontSize: 15 }} />;
    default:
      return <ReplyIcon sx={{ fontSize: 15 }} />;
  }
};

const nowLabel = () =>
  new Date().toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const todayLabel = () =>
  new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

// Renders the actual uploaded file when a preview URL is available (IMAGE/VIDEO
// inline, DOCUMENT as an openable link since a PDF can't be thumbnailed here),
// falling back to the placeholder icon otherwise — shared by the header and by
// each carousel card, which both store their media the same way.
const mediaPreviewBox = (format, url) => {
  const baseSx = {
    width: "100%",
    height: 90,
    mb: 0.5,
    borderRadius: "6px",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
  };

  if (!url) {
    return (
      <Box sx={{ ...baseSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {headerMediaIcon(format)}
      </Box>
    );
  }

  if (format === "IMAGE") {
    return (
      <Box
        component="img"
        src={url}
        alt="Media preview"
        sx={{ ...baseSx, objectFit: "cover", display: "block" }}
      />
    );
  }

  if (format === "VIDEO") {
    return <Box component="video" src={url} controls sx={{ ...baseSx, objectFit: "cover" }} />;
  }

  if (format === "DOCUMENT") {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          ...baseSx,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
          textDecoration: "none",
        }}
      >
        {headerMediaIcon(format)}
        <Typography sx={{ fontSize: 10, color: "primary.main" }}>View document</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...baseSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
      {headerMediaIcon(format)}
    </Box>
  );
};

const WhatsAppTemplatePreview = ({
  template,
  channelName = "Clusterden",
  headerPreviewUrl,
  carouselCardsMedia,
}) => {
  const components = template?.components || [];
  const headerComp = components.find((c) => c.type === "HEADER");
  const bodyComp = components.find((c) => c.type === "BODY");
  const footerComp = components.find((c) => c.type === "FOOTER");
  const buttonsComp = components.find((c) => c.type === "BUTTONS");
  const buttons = buttonsComp?.buttons || [];
  const headerFormat = headerComp?.format?.toUpperCase();
  const isMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat);
  const carouselComp = components.find((c) => c.type?.toUpperCase() === "CAROUSEL");
  const carouselCards = carouselComp?.cards || [];

  return (
    <Box
      sx={{
        width: 260,
        mx: "auto",
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      {/* Chat header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: "8px 10px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>
            {channelName.charAt(0).toUpperCase()}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {channelName}
          </Typography>
          <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.2 }} noWrap>
            Tap for contact info
          </Typography>
        </Box>
        <VideocamOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <CallOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
      </Box>

      {/* Chat body */}
      <Box
        sx={{
          backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#0b141a" : "#e8e0d5"),
          padding: "12px 10px",
          minHeight: 360,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 10,
              color: "text.secondary",
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: "6px",
              padding: "2px 8px",
            }}
          >
            {todayLabel()}
          </Typography>
        </Box>

        {!template ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Select a template to preview the message
          </Typography>
        ) : (
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: "8px",
              padding: "8px 10px",
              maxWidth: "90%",
              boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
            }}
          >
            {headerComp?.format === "TEXT" && headerComp.text && (
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                {headerComp.text}
              </Typography>
            )}
            {isMediaHeader && mediaPreviewBox(headerFormat, headerPreviewUrl)}
            {bodyComp?.text && (
              <Typography sx={{ fontSize: 13, whiteSpace: "pre-line" }}>{bodyComp.text}</Typography>
            )}
            {footerComp?.text && (
              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                {footerComp.text}
              </Typography>
            )}
            <Typography sx={{ fontSize: 10, color: "text.secondary", textAlign: "right", mt: 0.5 }}>
              {nowLabel()}
            </Typography>

            {carouselCards.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: "flex", gap: 0.75, overflowX: "auto", pb: 0.5 }}>
                  {carouselCards.map((card, index) => {
                    const cardHeader = card?.components?.find(
                      (c) => (c.type || "").toLowerCase() === "header"
                    );
                    const cardFormat = (cardHeader?.format || "IMAGE").toUpperCase();
                    const cardUrl = carouselCardsMedia?.[index]?.media?.mediaUrl;
                    return (
                      <Box key={`carousel-preview-${index}`} sx={{ width: 100, flexShrink: 0 }}>
                        {mediaPreviewBox(cardFormat, cardUrl)}
                      </Box>
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    padding: "6px 0",
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    color: "primary.main",
                  }}
                >
                  <ViewCarouselIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    Carousel ({carouselCards.length} card{carouselCards.length === 1 ? "" : "s"})
                  </Typography>
                </Box>
              </Box>
            )}

            {buttons.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {buttons.map((btn, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      padding: "6px 0",
                      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      color: "primary.main",
                    }}
                  >
                    {buttonIcon(btn.type)}
                    <Typography sx={{ fontSize: 12, color: "primary.main", fontWeight: 500 }}>
                      {btn.text || "Button"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Fake input bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: "8px 10px",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton size="small" disabled sx={{ padding: 0 }}>
          <AddIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            height: 22,
            borderRadius: "12px",
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1e2a30" : "#f0f0f0"),
          }}
        />
        <CameraAltOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <MicNoneOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      </Box>
    </Box>
  );
};

export default WhatsAppTemplatePreview;
